import { useEffect, useMemo, useState } from 'react';

export interface PopoverOrigin {
  vertical: 'top' | 'center' | 'bottom' | number;
  horizontal: 'left' | 'center' | 'right' | number;
}

interface UsePopoverPositionOptions {
  thresholdPercentage?: number; // Determines the edge area, default: 50%
  anchorOrigin?: PopoverOrigin; // Default placement if inside the safe zone
  transformOrigin?: PopoverOrigin; // Default placement if inside the safe zone
}

export const usePopoverPosition = (
  anchorEl: HTMLElement | null,
  {
    thresholdPercentage = 50,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'left' },
  }: UsePopoverPositionOptions = {}
) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (anchorEl) {
      setRect(anchorEl.getBoundingClientRect());
    }
  }, [anchorEl]);

  const {
    anchorOrigin: calculatedAnchorOrigin,
    transformOrigin: calculatedTransformOrigin,
  } = useMemo(() => {
    if (!rect) {
      return { anchorOrigin, transformOrigin };
    }

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Calculate absolute threshold values
    const thresholdX = (thresholdPercentage / 100) * windowWidth;
    const thresholdY = (thresholdPercentage / 100) * windowHeight;
    const safeZoneX = windowWidth - thresholdX;
    const safeZoneY = windowHeight - thresholdY;

    let { vertical } = anchorOrigin;
    let { horizontal } = anchorOrigin;

    let transformVertical: PopoverOrigin['vertical'] = transformOrigin.vertical;
    let transformHorizontal: PopoverOrigin['horizontal'] =
      transformOrigin.horizontal;

    // Determine horizontal position
    if (rect.left < thresholdX) {
      horizontal = 'left'; // Open to the right
      transformHorizontal = 'left';
    } else if (rect.right > safeZoneX) {
      horizontal = 'right'; // Open to the left
      transformHorizontal = 'right';
    }

    // Determine vertical position
    if (rect.top < thresholdY) {
      vertical = 'bottom'; // Open downward
      transformVertical = 'top';
    } else if (rect.bottom > safeZoneY) {
      vertical = 'top'; // Open upward
      transformVertical = 'bottom';
    }

    return {
      anchorOrigin: { vertical, horizontal },
      transformOrigin: {
        vertical: transformVertical,
        horizontal: transformHorizontal,
      },
    };
  }, [rect, thresholdPercentage, anchorOrigin]);

  return {
    anchorOrigin: calculatedAnchorOrigin,
    transformOrigin: calculatedTransformOrigin,
  };
};
